/**
 * HouseRules Step Component
 *
 * Step 7 of listing wizard: house rules and cancellation policy.
 */

import React from 'react';
import { ListingFormData, ListingHouseRules } from '../../hooks';

interface HouseRulesProps {
  data: ListingFormData;
  onChange: (updates: Partial<ListingFormData>) => void;
  onHouseRulesChange: (updates: Partial<ListingHouseRules>) => void;
}

const cancellationPolicies = [
  {
    id: 'flexible',
    name: 'Flexible',
    description: 'Full refund if cancelled 24 hours before check-in',
    details: [
      'Full refund up to 24 hours before check-in',
      '50% refund after that',
      'Free cancellation until 24 hours before',
    ],
  },
  {
    id: 'moderate',
    name: 'Moderate',
    description: 'Full refund if cancelled 5 days before check-in',
    details: [
      'Full refund up to 5 days before check-in',
      '50% refund up to 24 hours before',
      'No refund after check-in',
    ],
  },
  {
    id: 'strict',
    name: 'Strict',
    description: '50% refund if cancelled 7 days before check-in',
    details: [
      '50% refund up to 7 days before check-in',
      'No refund within 7 days of check-in',
      'Best for high-demand properties',
    ],
  },
];

export const HouseRules: React.FC<HouseRulesProps> = ({
  data,
  onChange,
  onHouseRulesChange,
}) => {
  const { houseRules } = data;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          House rules
        </h3>
        <p className="text-gray-600">
          Set expectations for guests staying at your property.
        </p>
      </div>

      {/* Standard Rules */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Standard rules</h4>

        <RuleToggle
          label="Smoking allowed"
          description="Guests can smoke inside the property"
          checked={houseRules.smokingAllowed}
          onChange={(checked) => onHouseRulesChange({ smokingAllowed: checked })}
          icon={<SmokingIcon className="w-5 h-5" />}
        />

        <RuleToggle
          label="Pets allowed"
          description="Guests can bring pets"
          checked={houseRules.petsAllowed}
          onChange={(checked) => onHouseRulesChange({ petsAllowed: checked })}
          icon={<PetIcon className="w-5 h-5" />}
        />

        <RuleToggle
          label="Parties allowed"
          description="Events and gatherings are permitted"
          checked={houseRules.partiesAllowed}
          onChange={(checked) => onHouseRulesChange({ partiesAllowed: checked })}
          icon={<PartyIcon className="w-5 h-5" />}
        />
      </div>

      {/* Additional Rules */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional rules
          <span className="text-gray-400 font-normal ml-2">(optional)</span>
        </label>
        <textarea
          value={houseRules.additionalRules}
          onChange={(e) =>
            onHouseRulesChange({ additionalRules: e.target.value })
          }
          placeholder="Any other rules or information guests should know..."
          rows={4}
          maxLength={1000}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
        />
        <p className="text-sm text-gray-500 mt-1">
          {houseRules.additionalRules.length}/1000 characters
        </p>
      </div>

      {/* Cancellation Policy */}
      <div>
        <h4 className="font-medium text-gray-900 mb-4">Cancellation policy</h4>
        <div className="space-y-3">
          {cancellationPolicies.map((policy) => (
            <button
              key={policy.id}
              type="button"
              onClick={() =>
                onChange({
                  cancellationPolicy: policy.id as 'flexible' | 'moderate' | 'strict',
                })
              }
              className={`w-full p-4 rounded-xl border-2 text-left transition-colors ${
                data.cancellationPolicy === policy.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">{policy.name}</span>
                {data.cancellationPolicy === policy.id && (
                  <CheckCircleIcon className="w-5 h-5 text-primary-500" />
                )}
              </div>
              <p className="text-sm text-gray-600 mb-2">{policy.description}</p>
              <ul className="text-xs text-gray-500 space-y-1">
                {policy.details.map((detail, idx) => (
                  <li key={idx}>• {detail}</li>
                ))}
              </ul>
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h4 className="font-medium text-gray-900 mb-2">Rules summary</h4>
        <div className="flex flex-wrap gap-2">
          <RuleBadge
            label={houseRules.smokingAllowed ? 'Smoking allowed' : 'No smoking'}
            positive={houseRules.smokingAllowed}
          />
          <RuleBadge
            label={houseRules.petsAllowed ? 'Pets welcome' : 'No pets'}
            positive={houseRules.petsAllowed}
          />
          <RuleBadge
            label={houseRules.partiesAllowed ? 'Parties OK' : 'No parties'}
            positive={houseRules.partiesAllowed}
          />
          <RuleBadge
            label={`${
              cancellationPolicies.find((p) => p.id === data.cancellationPolicy)?.name
            } cancellation`}
            positive={null}
          />
        </div>
      </div>
    </div>
  );
};

// Rule Toggle Component
interface RuleToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  icon: React.ReactNode;
}

const RuleToggle: React.FC<RuleToggleProps> = ({
  label,
  description,
  checked,
  onChange,
  icon,
}) => (
  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
    <div className="flex items-center gap-3">
      <div className="text-gray-400">{icon}</div>
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
    </label>
  </div>
);

// Rule Badge Component
interface RuleBadgeProps {
  label: string;
  positive: boolean | null;
}

const RuleBadge: React.FC<RuleBadgeProps> = ({ label, positive }) => (
  <span
    className={`px-3 py-1 rounded-full text-sm ${
      positive === null
        ? 'bg-gray-200 text-gray-700'
        : positive
        ? 'bg-green-100 text-green-700'
        : 'bg-red-100 text-red-700'
    }`}
  >
    {label}
  </span>
);

// Icons
const SmokingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6m12 0a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v4a2 2 0 002 2m12 0v6a2 2 0 01-2 2H8a2 2 0 01-2-2v-6" />
  </svg>
);

const PetIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
  </svg>
);

const PartyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
  </svg>
);

const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
  </svg>
);

export default HouseRules;
